import React, { useCallback, useContext, useState } from 'react';

import {
  StockAndPriceRow,
  StoreLogoColumn,
  HighlightContent,
  HighlightOfferCardHeader,
  HighlightOfferCardTitle,
  HighlightOfferCard,
  RightColumn,
} from './styles';
import {
  ActionArea,
  MiddleColumn,
  BottomMiddleRow,
  TopMiddleRow,
  ProductName,
  ProductNameColumn,
  Column,
  PaymentProvidersColumn,
  PriceLabelWrapper,
  ReportRow,
} from '../styles';
import Clickable from '@prisjakt/ui-clickable';
import { Badge } from '@prisjakt/ui/badge';
import PriceRowStoreInfoColumn from '../shared/store-info-column';
import { IOfferRowProps } from '../types';
import {
  getContainerProps,
  hasStoreIndicators,
  logRevenueEvent,
} from '../../utils';
import { useDispatch } from 'react-redux';
import {
  productClickOutBounce,
  productPriceClick,
} from 'src/areas/product/actions/product';
import { AppContext } from '@prisjakt/ui-app-context';
import { ProductPageProductClick, ReportRowType } from '../../types';
import StockPriceInfo from '../../components/stock-price-info';
import { getStoreRating, getValidPriceType } from '../../../utils';
import StoreTags from '../../components/store-tags';
import PaymentProviders from '../../components/payment-provider';
import CampaignBadge from 'src/components/campaign-badge';
import { PriceLabel } from '../../components/price-column';
import StockInfo from '../../components/stock-info';
import { StoreRatingAverage } from '../../components/store-rating-average';
import ReportButton from '../../components/report-button';

const HighlightedOfferRow: React.FC<IOfferRowProps> = props => {
  const { campaign, priceDisplay, productId, type, media } = props;
  const {
    price,
    name,
    store,
    externalUri,
    stock,
    deliveryOptions,
    brandRecommendedRetailer,
    isMemberPrice,
  } = props.offer;
  const [priceRowReport, setPriceRowReport] = useState(ReportRowType.NONE);
  const [reportRowId, setReportRowId] = useState<string | null>(null);
  const priceType = getValidPriceType(props.offer.type);
  const dispatch = useDispatch();
  const { market } = useContext(AppContext);
  const small = props.highlightCount > 1;
  const large = !small;

  const isInternationalPrice = store?.market === 'secondary';

  const onClickOutBounce = useCallback(() => {
    dispatch(productClickOutBounce(productId, props.offer.id));
  }, [dispatch, productId, props.offer.id]);

  const handleClickOut = () => {
    if (props.offer.id) {
      setPriceRowReport(ReportRowType.DEFAULT);
      logRevenueEvent(props.offer.id, productId);

      if (IS_BROWSER) {
        startBounceTimer(props.offer.id);
      }

      const dispatchParams = productPriceClick(
        productId,
        props.offer.price,
        featured,
        ProductPageProductClick,
        false,
        type
      );
      dispatch(dispatchParams);
    }
  };

  const startBounceTimer = useCallback(
    (rowId: string) => {
      // Start a timer when a user clicks out on a product
      // if they return to Prisjakt within 15 seconds
      // display an error report button in the price row.

      const startTime = Date.now();

      const onRefocus = () => {
        if (
          Date.now() - startTime > 15000 &&
          document.visibilityState === 'visible'
        ) {
          document.removeEventListener('visibilitychange', onRefocus);
          return;
        }

        if (document.visibilityState === 'visible') {
          setReportRowId(rowId);

          // Action event for tracking
          onClickOutBounce();

          document.removeEventListener('visibilitychange', onRefocus);
        }
      };

      document.addEventListener('visibilitychange', onRefocus);
    },
    [onClickOutBounce, setReportRowId]
  );

  const closeReportOverlay = useCallback(() => {
    setPriceRowReport(ReportRowType.NONE);
    setReportRowId(null);
  }, [setReportRowId]);

  if (!store || !props.offer.id) {
    return null;
  }

  const rating = getStoreRating(store);
  const featured = Boolean(store?.featured);

  const storeIndicatorsExist = hasStoreIndicators(
    store,
    isInternationalPrice,
    market,
    deliveryOptions,
    brandRecommendedRetailer
  );

  const containerProps = getContainerProps(
    name || '',
    featured,
    handleClickOut,
    externalUri
  );

  return (
    <HighlightOfferCard type={type}>
      <ActionArea container={Clickable} containerProps={containerProps}>
        <HighlightOfferCardHeader>
          <HighlightOfferCardTitle>{props.title}</HighlightOfferCardTitle>
        </HighlightOfferCardHeader>
        <HighlightContent>
          {large && (
            <div>
              <PriceRowStoreInfoColumn store={store} rating={rating} />
            </div>
          )}
          <MiddleColumn hoverEnabled>
            <TopMiddleRow>
              <ProductNameColumn featured={featured}>
                <ProductName showAsMobile={small} featured={featured}>
                  {name}
                </ProductName>
              </ProductNameColumn>
              {large && (
                <StockPriceInfo
                  featured={featured}
                  campaign={campaign}
                  price={price}
                  priceDisplay={priceDisplay}
                  priceType={priceType}
                  stock={stock}
                  isMemberPrice={Boolean(isMemberPrice)}
                />
              )}
              {small && (
                <RightColumn>
                  <StockAndPriceRow>
                    {featured && (
                      <StockInfo
                        status={stock && stock.status}
                        statusText={stock && stock.statusText}
                        toneDown={!featured}
                      />
                    )}
                    <PriceLabelWrapper>
                      {campaign && (
                        <CampaignBadge colors={campaign.media.colors} />
                      )}
                      {featured && Boolean(isMemberPrice) && (
                        <Badge color="GREY_INVERTED" text="Members" />
                      )}
                      {price && (
                        <PriceLabel
                          price={price}
                          priceDisplay={priceDisplay}
                          storeIsFeatured={featured}
                          isCampaign={Boolean(campaign)}
                        />
                      )}
                    </PriceLabelWrapper>
                  </StockAndPriceRow>
                </RightColumn>
              )}
            </TopMiddleRow>
            <BottomMiddleRow
              featured
              hasStoreIndicators={storeIndicatorsExist || small}
            >
              <Column>
                {small && (
                  <StoreLogoColumn>
                    <PriceRowStoreInfoColumn store={store} rating={rating} />
                    <StoreRatingAverage
                      store={store}
                      rating={rating}
                      showReviewCount={false}
                    />
                  </StoreLogoColumn>
                )}
                {store && (
                  <PaymentProvidersColumn>
                    <PaymentProviders store={store} limit={small ? 1 : 4} />
                  </PaymentProvidersColumn>
                )}
              </Column>
              <Column>
                <StoreTags
                  countryCode={store && store.countryCode}
                  isInternationalPrice={isInternationalPrice}
                  deliveryOptions={deliveryOptions}
                  brandRecommendedRetailer={brandRecommendedRetailer}
                  showCircles={small}
                />
              </Column>
            </BottomMiddleRow>
            {priceRowReport === ReportRowType.DEFAULT &&
              reportRowId === props.offer.id && (
                <ReportRow>
                  <ReportButton
                    store={store}
                    priceId={props.offer.id}
                    priceValue={price && price.exclShipping}
                    stockStatus={stock && stock.status}
                    productImage={media.first}
                    onClose={closeReportOverlay}
                    productName={name}
                  />
                </ReportRow>
              )}
          </MiddleColumn>
        </HighlightContent>
      </ActionArea>
    </HighlightOfferCard>
  );
};

export default HighlightedOfferRow;
